import { Box, Typography } from "@mui/material";
import type { Components } from "react-markdown";

export type BizmedAiMarkdownVariant = "clinical" | "chatbubble";

const clinical: Components = {
  h1: ({ children }) => (
    <Typography component="h1" variant="h6" sx={{ fontWeight: 800, mt: 2.5, mb: 1.25, color: "#18212f" }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 800, mt: 2.5, mb: 1.25, color: "#18212f" }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      component="h3"
      variant="subtitle1"
      sx={{
        fontWeight: 800,
        mt: 2.75,
        mb: 1.25,
        color: "#18212f",
        fontSize: "1.05rem",
        lineHeight: 1.4,
      }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography
      component="h4"
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        mt: 2,
        mb: 1,
        color: "#334155",
        fontSize: "0.95rem",
        lineHeight: 1.4,
      }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography
      component="p"
      variant="body2"
      sx={{
        mb: 1.35,
        lineHeight: 1.75,
        color: "#455468",
        "&:last-child": { mb: 0 },
      }}
    >
      {children}
    </Typography>
  ),
  br: () => <br />,
  ul: ({ children }) => (
    <Box
      component="ul"
      sx={{
        pl: 2.5,
        my: 1.5,
        color: "#455468",
        listStyleType: "disc",
        "& ul": { listStyleType: "circle", mt: 0.75, mb: 0.75 },
      }}
    >
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box
      component="ol"
      sx={{
        pl: 2.5,
        my: 1.5,
        color: "#455468",
        listStyleType: "decimal",
        "& ol": { listStyleType: "lower-alpha", mt: 0.75, mb: 0.75 },
      }}
    >
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box
      component="li"
      sx={{
        mb: 1,
        lineHeight: 1.75,
        fontSize: 14,
        display: "list-item",
        "& > p": { mb: 0.85, mt: 0.15 },
        "& > p:first-of-type": { mt: 0 },
        "& > p:last-child": { mb: 0 },
      }}
    >
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Typography component="strong" sx={{ fontWeight: 700, color: "#334155", display: "inline" }}>
      {children}
    </Typography>
  ),
  em: ({ children }) => (
    <Typography component="em" sx={{ fontStyle: "italic", color: "#455468", display: "inline" }}>
      {children}
    </Typography>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{
        border: "none",
        borderTop: "1px solid #e2e8f0",
        my: 2.5,
        display: "block",
      }}
    />
  ),
};

const chatbubble: Components = {
  h1: ({ children }) => (
    <Typography component="h1" variant="subtitle2" sx={{ fontWeight: 800, mt: 1.25, mb: 0.5, color: "#111827" }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 800, mt: 1.25, mb: 0.5, color: "#111827" }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      component="h3"
      variant="subtitle2"
      sx={{ fontWeight: 800, mt: 1.1, mb: 0.45, color: "#111827", fontSize: "0.9rem", lineHeight: 1.35 }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography
      component="h4"
      variant="body2"
      sx={{ fontWeight: 700, mt: 0.9, mb: 0.35, color: "#374151", fontSize: "0.8125rem", lineHeight: 1.35 }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography
      component="p"
      variant="body2"
      sx={{
        mb: 0.65,
        lineHeight: 1.6,
        color: "inherit",
        fontSize: "0.8125rem",
        "&:last-child": { mb: 0 },
      }}
    >
      {children}
    </Typography>
  ),
  br: () => <br />,
  ul: ({ children }) => (
    <Box
      component="ul"
      sx={{
        pl: 2,
        my: 0.65,
        color: "inherit",
        listStyleType: "disc",
        fontSize: "0.8125rem",
        "& ul": { listStyleType: "circle", mt: 0.35, mb: 0.35 },
      }}
    >
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box
      component="ol"
      sx={{
        pl: 2,
        my: 0.65,
        color: "inherit",
        listStyleType: "decimal",
        fontSize: "0.8125rem",
        "& ol": { listStyleType: "lower-alpha", mt: 0.35, mb: 0.35 },
      }}
    >
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box
      component="li"
      sx={{
        mb: 0.4,
        lineHeight: 1.6,
        fontSize: "0.8125rem",
        display: "list-item",
        color: "inherit",
        "& > p": { mb: 0.4, mt: 0.1 },
        "& > p:first-of-type": { mt: 0 },
        "& > p:last-child": { mb: 0 },
      }}
    >
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Typography component="strong" sx={{ fontWeight: 700, color: "inherit", display: "inline" }}>
      {children}
    </Typography>
  ),
  em: ({ children }) => (
    <Typography component="em" sx={{ fontStyle: "italic", color: "inherit", display: "inline", opacity: 0.95 }}>
      {children}
    </Typography>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{
        border: "none",
        borderTop: "1px solid rgba(0,0,0,0.08)",
        my: 1.25,
        display: "block",
      }}
    />
  ),
};

const byVariant: Record<BizmedAiMarkdownVariant, Components> = {
  clinical,
  chatbubble,
};

export function getBizmedAiMarkdownComponents(variant: BizmedAiMarkdownVariant = "clinical"): Components {
  return byVariant[variant];
}
